import React, { useState, useEffect, useRef } from 'react'
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Progress } from "@/components/ui/progress"
import { ScrollArea } from "@/components/ui/scroll-area"
import { 
  Database, 
  ArrowRight, 
  Loader2, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  ChevronDown, 
  Clock, 
  Eye,
  Play,
  Download,
  Merge,
  Activity
} from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'


interface StagingSyncResult {
  success: boolean
  total_mailboxes_fetched: number
  total_written_to_staging: number
  total_failed: number
  errors: string[]
  timestamp: string
}

interface MergeResult {
  matched: number
  updated: number
  skipped: number
  not_found: string[]
  timestamp: string
}

interface ActivityLog {
  timestamp: string
  message: string
  type: 'info' | 'success' | 'error' | 'warning'
}

export function StagingSyncManagement() {
  const queryClient = useQueryClient()
  const [isStagingSync, setIsStagingSync] = useState(false)
  const [isMerging, setIsMerging] = useState(false)
  const [stagingResult, setStagingResult] = useState<StagingSyncResult | null>(null)
  const [mergeResult, setMergeResult] = useState<MergeResult | null>(null)
  const [showDetails, setShowDetails] = useState(false)
  const [stagingCount, setStagingCount] = useState<number | null>(null)
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([])
  const [syncProgress, setSyncProgress] = useState({ fetched: 0, written: 0, errors: 0, expected: 586 })
  const [mergeProgress, setMergeProgress] = useState({ matched: 0, updated: 0, skipped: 0, notFound: 0, total: 0 })
  const logPollRef = useRef<NodeJS.Timeout | null>(null)

  const checkStagingCount = async () => {
    try {
      console.log('Fetching mailbox sync status...')
      // smartlead_mailbox_staging table doesn't exist - using mock data
      const count = 0
      console.log('Note: smartlead_mailbox_staging table not available, using mock count')
      
      console.log('Setting staging count to:', count || 0)
      setStagingCount(count || 0)
    } catch (error) {
      console.error('Error checking staging count:', error)
      setStagingCount(0) // Set to 0 on error to show something
    }
  }

  const loadHistoricalLogs = async () => {
    try {
      // Fetch last 100 activity logs from staging and merge operations
      const { data: activities, error } = await supabase
        .from('recent_activity')
        .select('*')
        .in('activity_type', ['staging_sync', 'staging_insert', 'smartlead_fetch', 'merge-operation'])
        .order('created_at', { ascending: false })
        .limit(100)

      if (error) {
        console.error('Error fetching historical logs:', error)
        return
      }

      if (activities && activities.length > 0) {
        const historicalLogs = activities.map(activity => {
          const date = new Date(activity.created_at)
          const timestamp = `${date.getDate().toString().padStart(2, '0')}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getFullYear()} ${date.toLocaleTimeString()}`
          return {
            timestamp,
            message: `${activity.icon || '🔄'} ${activity.title}: ${activity.description}`,
            type: (activity.title.includes('Completed') || activity.title.includes('Success') ? 'success' : 
                  activity.title.includes('Failed') || activity.title.includes('Error') ? 'error' : 
                  activity.title.includes('Warning') ? 'warning' : 'info') as ActivityLog['type']
          }
        })
        
        setActivityLogs(historicalLogs)
      }
    } catch (error) {
      console.error('Error loading historical logs:', error)
    }
  }

  const addActivityLog = (message: string, type: ActivityLog['type'] = 'info') => {
    const now = new Date()
    const timestamp = `${now.getDate().toString().padStart(2, '0')}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getFullYear()} ${now.toLocaleTimeString()}`
    const log: ActivityLog = {
      timestamp,
      message,
      type
    }
    setActivityLogs(prev => {
      // Check if this exact message already exists to avoid duplicates
      const exists = prev.some(existingLog => existingLog.message === log.message)
      if (exists) return prev
      
      return [log, ...prev.slice(0, 99)] // Keep last 100 logs
    })
  }

  const pollFunctionLogs = async () => {
    try {
      // Fetch real-time activity logs for staging sync
      const { data: activities, error } = await supabase
        .from('recent_activity')
        .select('*')
        .in('activity_type', ['staging_sync', 'staging_insert', 'smartlead_fetch'])
        .gte('created_at', new Date(syncStartTime.current || Date.now() - 300000).toISOString())
        .order('created_at', { ascending: false })
        .limit(20)

      if (error) {
        console.error('Error fetching staging activities:', error)
        return
      }

      if (activities && activities.length > 0) {
        for (const activity of activities) {
          const logMessage = `${activity.icon || '🔄'} ${activity.title}: ${activity.description}`
          
          // Add new activities to log (addActivityLog handles duplicates)
          const date = new Date(activity.created_at)
          const timestamp = `${date.getDate().toString().padStart(2, '0')}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getFullYear()} ${date.toLocaleTimeString()}`
          const activityLog: ActivityLog = {
            timestamp,
            message: logMessage,
            type: activity.title.includes('Completed') || activity.title.includes('Success') ? 'success' : 
                  activity.title.includes('Failed') || activity.title.includes('Error') ? 'error' : 'info'
          }
          
          setActivityLogs(prev => {
            const exists = prev.some(log => log.message === logMessage)
            if (!exists) {
              return [activityLog, ...prev.slice(0, 99)]
            }
            return prev
          })

          // Extract metrics from activity descriptions
          if (activity.description.includes('Fetched') && activity.description.includes('Written')) {
            const fetchMatch = activity.description.match(/Fetched: (\d+)/)
            const writtenMatch = activity.description.match(/Written: (\d+)/)
            const errorMatch = activity.description.match(/Errors: (\d+)/)
            
            if (fetchMatch || writtenMatch) {
              setSyncProgress(prev => ({
                ...prev,
                fetched: fetchMatch ? parseInt(fetchMatch[1]) : prev.fetched,
                written: writtenMatch ? parseInt(writtenMatch[1]) : prev.written,
                errors: errorMatch ? parseInt(errorMatch[1]) : prev.errors
              }))
            }
          }
        }
      }
    } catch (error) {
      console.error('Error polling staging logs:', error)
    }
  }
  
  const syncStartTime = useRef<number | null>(null)

  const pollMergeLogs = async () => {
    try {
      // Read actual merge activity logs from recent_activity table
      const { data: activities, error } = await supabase
        .from('recent_activity')
        .select('*')
        .eq('activity_type', 'merge-operation')
        .gte('created_at', new Date(mergeStartTime.current || Date.now() - 300000).toISOString())
        .order('created_at', { ascending: false })
        .limit(20)

      if (error) {
        console.error('Error fetching merge activities:', error)
        return
      }

      if (activities && activities.length > 0) {
        for (const activity of activities) {
          const logMessage = `${activity.icon || '🔄'} ${activity.title}: ${activity.description}`
          
          // Add new activities to log (avoiding duplicates)
          setActivityLogs(prev => {
            const exists = prev.some(log => log.message === logMessage)
            if (!exists) {
              const date = new Date(activity.created_at)
              const timestamp = `${date.getDate().toString().padStart(2, '0')}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getFullYear()} ${date.toLocaleTimeString()}`
              const activityLog: ActivityLog = {
                timestamp,
                message: logMessage,
                type: activity.title.includes('Completed') || activity.title.includes('Success') ? 'success' : 
                      activity.title.includes('Failed') || activity.title.includes('Error') ? 'error' : 'info'
              }
              return [activityLog, ...prev.slice(0, 99)]
            }
            return prev
          })

          // Extract detailed metrics from merge completion
          if (activity.description.includes('Matched:') && activity.description.includes('Updated:')) {
            const matchedMatch = activity.description.match(/Matched: (\d+)/)
            const updatedMatch = activity.description.match(/Updated: (\d+)/)
            const skippedMatch = activity.description.match(/Skipped: (\d+)/)
            
            if (matchedMatch || updatedMatch) {
              setMergeProgress(prev => ({
                ...prev,
                matched: matchedMatch ? parseInt(matchedMatch[1]) : prev.matched,
                updated: updatedMatch ? parseInt(updatedMatch[1]) : prev.updated,
                skipped: skippedMatch ? parseInt(skippedMatch[1]) : prev.skipped
              }))
            }
          }
        }
      }
    } catch (error) {
      console.error('Error in merge progress polling:', error)
    }
  }
  
  const mergeStartTime = useRef<number | null>(null)

  const startLogPolling = () => {
    if (logPollRef.current) clearInterval(logPollRef.current)
    
    syncStartTime.current = Date.now()
    logPollRef.current = setInterval(pollFunctionLogs, 2000) // Check every 2 seconds
    
    // Stop polling after 5 minutes
    setTimeout(() => {
      if (logPollRef.current) {
        clearInterval(logPollRef.current)
        logPollRef.current = null
      }
    }, 300000)
  }

  const stopLogPolling = () => {
    if (logPollRef.current) {
      clearInterval(logPollRef.current)
      logPollRef.current = null
    }
  }

  const startMergeLogPolling = () => {
    if (logPollRef.current) clearInterval(logPollRef.current)
    
    mergeStartTime.current = Date.now()
    logPollRef.current = setInterval(pollMergeLogs, 2000) // Use same polling ref
    
    // Stop polling after 5 minutes
    setTimeout(() => {
      if (logPollRef.current) {
        clearInterval(logPollRef.current)
        logPollRef.current = null
      }
    }, 300000)
  }

  const stopMergeLogPolling = () => {
    if (logPollRef.current) {
      clearInterval(logPollRef.current)
      logPollRef.current = null
    }
  }

  // Load historical logs on component mount and start continuous log polling
  useEffect(() => {
    checkStagingCount()
    loadHistoricalLogs() // Load historical logs first
    
    // Start continuous polling for new activity logs
    const continuousLogPoll = setInterval(() => {
      pollFunctionLogs()
      pollMergeLogs()
    }, 3000) // Poll every 3 seconds for always-visible feed
    
    return () => {
      clearInterval(continuousLogPoll)
      stopLogPolling()
      stopMergeLogPolling()
    }
  }, [])

  const runStagingSync = async () => {
    setIsStagingSync(true)
    setStagingResult(null)
    setSyncProgress({ fetched: 0, written: 0, errors: 0, expected: 586 })
    
    addActivityLog('🚀 Starting Smartlead staging sync...', 'info')
    
    const loadingToastId = toast.loading('📥 Fetching all mailboxes from Smartlead...', {
      duration: Infinity
    })

    // Start enhanced log polling for staging
    startLogPolling()

    try {
      console.log('Starting staging sync...')
      addActivityLog('🔄 Calling smartlead-mailboxes function for full sync...', 'info')
      
      const { data, error } = await supabase.functions.invoke('smartlead-mailboxes', {
        body: { action: 'full-sync' }
      })

      if (error) throw error

      setStagingResult({
        success: data.success,
        total_mailboxes_fetched: data.total_fetched || 0,
        total_written_to_staging: data.total_inserted || 0,
        total_failed: data.insert_errors || 0,
        errors: [],
        timestamp: new Date().toISOString()
      })
      
      await checkStagingCount() // Refresh staging count
      
      toast.dismiss(loadingToastId)
      stopLogPolling()
      
      if (data.success) {
        const finalMessage = `✅ Staging sync completed! Fetched: ${data.total_fetched}, Written: ${data.total_inserted}, Errors: ${data.insert_errors || 0}`
        addActivityLog(finalMessage, 'success')
        toast.success('✅ Staging sync completed!', {
          description: `Fetched ${data.total_fetched} mailboxes, wrote ${data.total_inserted} to staging`
        })
      } else {
        addActivityLog(`❌ Staging sync failed: ${data.error || 'Unknown error'}`, 'error')
        toast.error('❌ Staging sync failed', {
          description: data.error || 'Unknown error occurred'
        })
      }
    } catch (error) {
      stopLogPolling()
      addActivityLog(`💥 Sync failed: ${error.message || 'Unknown error'}`, 'error')
      console.error('Staging sync error:', error)
      toast.dismiss(loadingToastId)
      toast.error('❌ Staging sync failed', {
        description: 'Failed to fetch mailboxes from Smartlead'
      })
      
      setStagingResult({
        success: false,
        total_mailboxes_fetched: 0,
        total_written_to_staging: 0,
        total_failed: 1,
        errors: [error.message || 'Unknown error'],
        timestamp: new Date().toISOString()
      })
    } finally {
      setIsStagingSync(false)
    }
  }

  const runMergeSync = async () => {
    addActivityLog('🔄 Starting merge to mailboxes...', 'info')
    setIsMerging(true)
    setMergeResult(null)
    setMergeProgress({ matched: 0, updated: 0, skipped: 0, notFound: 0, total: stagingCount || 0 })
    
    if (stagingCount === 0) {
      addActivityLog('⚠️ No staging data found - run staging sync first', 'warning')
      toast.error('❌ No staging data found', {
        description: 'Run staging sync first to populate staging table'
      })
      setIsMerging(false)
      return
    }

    const loadingToastId = toast.loading('🔄 Merging staging data to mailboxes...', {
      duration: Infinity
    })

    // Start enhanced merge log polling
    startMergeLogPolling()

    try {
      console.log('Starting merge sync...')
      addActivityLog('📞 Calling merge-smartlead-to-mailboxes function...', 'info')
      
      const { data, error } = await supabase.functions.invoke('merge-smartlead-to-mailboxes')

      if (error) {
        console.error('Supabase function error:', error)
        throw new Error(`Edge Function error: ${error.message}`)
      }

      if (!data) {
        throw new Error('No response data received from Edge Function')
      }

      console.log('Merge function response:', data)

      if (data.success && data.matched !== undefined) {
        setMergeResult(data)
        const finalMessage = `✅ Merge completed! Matched: ${data.matched}, Updated: ${data.updated}, Skipped: ${data.skipped}, Not Found: ${data.not_found?.length || 0}`
        addActivityLog(finalMessage, 'success')
        toast.dismiss(loadingToastId)
        toast.success('✅ Merge completed!', {
          description: `Matched: ${data.matched}, Updated: ${data.updated}, Skipped: ${data.skipped}`
        })
      } else {
        addActivityLog(`❌ Merge failed: ${data.error || 'Unknown error'}`, 'error')
        toast.dismiss(loadingToastId)
        toast.error('❌ Merge failed', {
          description: data.error || 'Unknown error occurred'
        })
      }
      
      // Invalidate mailboxes cache to refresh the UI
      queryClient.invalidateQueries({ queryKey: ['mailboxes'] })
      
      stopMergeLogPolling()
      
    } catch (error) {
      addActivityLog(`💥 Merge failed: ${error.message || 'Unknown error'}`, 'error')
      console.error('Merge sync error:', error)
      toast.dismiss(loadingToastId)
      toast.error('❌ Merge failed', {
        description: error.message || 'Failed to merge staging data to mailboxes'
      })
      
      setMergeResult({
        matched: 0,
        updated: 0,
        skipped: 0,
        not_found: [],
        timestamp: new Date().toISOString()
      })
    } finally {
      setIsMerging(false)
      stopMergeLogPolling()
    }
  }

  const runFullSync = async () => {
    // Use the same reliable two-step process as daily-full-sync
    addActivityLog('🚀 Starting full sync (fetch + merge)...', 'info')
    
    try {
      // Step 1: Fetch all mailboxes from Smartlead
      addActivityLog('📥 Fetching all mailboxes from Smartlead...', 'info')
      
      const { data: fetchData, error: fetchError } = await supabase.functions.invoke('smartlead-mailboxes', {
        body: { action: 'full-sync' }
      })

      if (fetchError) {
        console.error('Mailbox fetch failed:', fetchError)
        addActivityLog('❌ Failed to fetch mailboxes from Smartlead', 'error')
        toast.error(`Fetch failed: ${fetchError.message}`)
        return
      }

      addActivityLog(`✅ Successfully fetched ${fetchData?.total_fetched || 0} mailboxes`, 'success')

      // Step 2: Merge data into mailboxes table
      addActivityLog('🔄 Starting merge to mailboxes table...', 'info')
      
      const { data: mergeData, error: mergeError } = await supabase.functions.invoke('merge-smartlead-to-mailboxes', {
        body: {}
      })

      if (mergeError) {
        console.error('Merge failed:', mergeError)
        addActivityLog('❌ Failed to merge data to mailboxes', 'error')
        toast.error(`Merge failed: ${mergeError.message}`)
        return
      }

      addActivityLog(`✅ Full sync completed successfully! Updated ${mergeData?.updated || 0} mailboxes`, 'success')
      toast.success('Full sync completed successfully!')

    } catch (error) {
      console.error('Full sync error:', error)
      addActivityLog('❌ Full sync failed with error', 'error')
      toast.error('Full sync failed')
    }
  }

  return (
    <Card className="bg-card border shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-foreground">
          <Database className="h-5 w-5 text-primary" />
          Smartlead Staging Sync
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-sm text-muted-foreground">
          New two-phase sync system: fetch all Smartlead data to staging, then merge to mailboxes
        </div>

        {/* Staging Progress Display */}
        {isStagingSync && (
          <div className="space-y-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-blue-900">Staging Sync Progress</span>
              <div className="flex gap-4 text-xs text-blue-700">
                <Badge variant="secondary">Fetched: {syncProgress.fetched}</Badge>
                <Badge variant="secondary">Written: {syncProgress.written}</Badge>
                {syncProgress.errors > 0 && <Badge variant="destructive">Errors: {syncProgress.errors}</Badge>}
              </div>
            </div>
            <Progress 
              value={(syncProgress.fetched / syncProgress.expected) * 100} 
              className="h-2"
            />
          </div>
        )}

        {/* Merge Progress Display */}
        {isMerging && (
          <div className="space-y-3 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-green-900">Merge Progress</span>
              <div className="flex gap-2 text-xs text-green-700">
                <Badge variant="secondary">Matched: {mergeProgress.matched}</Badge>
                <Badge variant="secondary">Updated: {mergeProgress.updated}</Badge>
                <Badge variant="secondary">Skipped: {mergeProgress.skipped}</Badge>
                {mergeProgress.notFound > 0 && <Badge variant="outline">Not Found: {mergeProgress.notFound}</Badge>}
              </div>
            </div>
            <Progress 
              value={mergeProgress.total > 0 ? ((mergeProgress.matched + mergeProgress.skipped) / mergeProgress.total) * 100 : 0} 
              className="h-2"
            />
          </div>
        )}

        {/* Staging Count Display */}
        <div className="flex items-center justify-between p-3 bg-muted/20 rounded-lg border">
          <div className="flex items-center gap-2">
            <Database className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">Staging Records:</span>
          </div>
          <Badge variant="outline" className="font-mono">
            {stagingCount !== null ? stagingCount.toLocaleString() : 'Loading...'}
          </Badge>
        </div>
        
        {/* Always Visible Live Activity Feed */}
        <Card className="border-2 border-blue-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Activity className="h-4 w-4 text-blue-600" />
              Live Activity Feed
              {activityLogs.length > 0 && (
                <Badge variant="secondary" className="text-xs ml-auto">
                  {activityLogs.length} logs
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <ScrollArea className="h-48">
              {activityLogs.length === 0 ? (
                <div className="text-center text-muted-foreground text-sm py-4">
                  No activity yet. Start a sync to see real-time updates.
                </div>
              ) : (
                <div className="space-y-2">
                  {activityLogs.map((log, index) => (
                    <div 
                      key={index} 
                      className={`text-xs p-2 rounded flex items-start gap-2 ${
                        log.type === 'success' ? 'bg-green-50 text-green-800' :
                        log.type === 'error' ? 'bg-red-50 text-red-800' :
                        log.type === 'warning' ? 'bg-yellow-50 text-yellow-800' :
                        'bg-gray-50 text-gray-700'
                      }`}
                    >
                       <span className="text-xs opacity-70 font-mono min-w-[140px]">
                         {log.timestamp}
                       </span>
                      <span className="flex-1">{log.message}</span>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="grid grid-cols-1 gap-3">
          {/* Full Sync (Both Steps) */}
          <Button 
            onClick={runFullSync} 
            disabled={isStagingSync || isMerging}
            className="w-full"
            size="lg"
          >
            {(isStagingSync || isMerging) ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {isStagingSync ? 'Fetching from Smartlead...' : 'Merging to Mailboxes...'}
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                Run Full Sync (Staging + Merge)
              </>
            )}
          </Button>

          <div className="grid grid-cols-2 gap-2">
            {/* Step 1: Staging Sync */}
            <Button 
              onClick={runStagingSync} 
              disabled={isStagingSync || isMerging}
              variant="outline"
            >
              {isStagingSync ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Fetching...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  1. Fetch to Staging
                </>
              )}
            </Button>

            {/* Step 2: Merge Sync */}
            <Button 
              onClick={runMergeSync} 
              disabled={isStagingSync || isMerging || stagingCount === 0}
              variant="outline"
            >
              {isMerging ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Merging...
                </>
              ) : (
                <>
                  <Merge className="h-4 w-4 mr-2" />
                  2. Merge to Mailboxes
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Results Section */}
        {(stagingResult || mergeResult) && (
          <Collapsible open={showDetails} onOpenChange={setShowDetails}>
            <CollapsibleTrigger asChild>
              <Button variant="outline" className="w-full">
                <Eye className="h-4 w-4 mr-2" />
                View Sync Results
                <ChevronDown className={`h-4 w-4 ml-auto transition-transform ${showDetails ? 'rotate-180' : ''}`} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-4">
              <div className="space-y-4">
                
                {/* Staging Results */}
                {stagingResult && (
                  <div className="border rounded-lg p-4 bg-muted/5">
                    <div className="flex items-center gap-2 mb-3">
                      <Download className="h-4 w-4" />
                      <h4 className="font-medium text-sm">Staging Sync Results</h4>
                      {stagingResult.success ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-600" />
                      )}
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600">
                          {stagingResult.total_mailboxes_fetched}
                        </div>
                        <div className="text-muted-foreground">Fetched</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">
                          {stagingResult.total_written_to_staging}
                        </div>
                        <div className="text-muted-foreground">Written</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-red-600">
                          {stagingResult.total_failed}
                        </div>
                        <div className="text-muted-foreground">Failed</div>
                      </div>
                    </div>

                    {stagingResult.errors.length > 0 && (
                      <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-xs">
                        <div className="font-medium text-red-800 mb-1">Errors:</div>
                        {stagingResult.errors.slice(0, 3).map((error, index) => (
                          <div key={index} className="text-red-700">{error}</div>
                        ))}
                        {stagingResult.errors.length > 3 && (
                          <div className="text-red-600">...and {stagingResult.errors.length - 3} more</div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Merge Results */}
                {mergeResult && (
                  <div className="border rounded-lg p-4 bg-muted/5">
                    <div className="flex items-center gap-2 mb-3">
                      <Merge className="h-4 w-4" />
                      <h4 className="font-medium text-sm">Merge Results</h4>
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    </div>
                    
                    <div className="grid grid-cols-4 gap-4 text-sm">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600">
                          {mergeResult.matched}
                        </div>
                        <div className="text-muted-foreground">Matched</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">
                          {mergeResult.updated}
                        </div>
                        <div className="text-muted-foreground">Updated</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-yellow-600">
                          {mergeResult.skipped}
                        </div>
                        <div className="text-muted-foreground">Skipped</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-red-600">
                          {mergeResult.not_found.length}
                        </div>
                        <div className="text-muted-foreground">Not Found</div>
                      </div>
                    </div>

                    {mergeResult.not_found.length > 0 && (
                      <div className="mt-3">
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button variant="outline" size="sm" className="w-full">
                              <AlertTriangle className="h-3 w-3 mr-2" />
                              View {mergeResult.not_found.length} Not Found Emails
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-2xl max-h-[60vh] overflow-y-auto">
                            <DialogHeader>
                              <DialogTitle>Emails Not Found in Mailboxes Table</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-2 mt-4">
                              {mergeResult.not_found.map((email, index) => (
                                <div key={index} className="p-2 bg-orange-50 border border-orange-200 rounded text-sm">
                                  {email}
                                </div>
                              ))}
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    )}
                  </div>
                )}

                {/* Sync Flow Visualization */}
                <div className="border rounded-lg p-4 bg-blue-50/30">
                  <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                    <ArrowRight className="h-4 w-4" />
                    Sync Flow
                  </h4>
                  <div className="flex items-center gap-2 text-xs">
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <span>Smartlead API</span>
                    </div>
                    <ArrowRight className="h-3 w-3" />
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                      <span>Staging Table</span>
                    </div>
                    <ArrowRight className="h-3 w-3" />
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      <span>Mailboxes Table</span>
                    </div>
                  </div>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}
      </CardContent>
    </Card>
  )
}